import React, { useEffect, useState } from 'react';
import { useLocation, useOutletContext, Outlet, Link } from 'react-router-dom';
import useThemeMediaQuery from '../../@BASE/hooks/useThemeMediaQuery';
import BASEPageSimple from '../../@BASE/core/BASEPageSimple';
import CommentList from '../shared-components/comments/CommentList';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/userSlice';
import authRoles from '../auth/authRoles';
// BASE Components
import BASESvgIcon from '../../@BASE/core/BASESvgIcon';
import BASELoading from '../../@BASE/core/BASELoading';
import NavLinkAdapter from '../../@BASE/core/NavLinkAdapter/NavLinkAdapter';
// Material UI Components
import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Tooltip } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import moment from 'moment';

const Root = styled(BASEPageSimple)(({ theme }) => ({
  '& .BASEPageSimple-content': {},
  '& .BASEPageSimple-sidebarHeader': {},
  '& .BASEPageSimple-sidebarContent': {}
}));

function InstanceComments({ category, hasSidebar = false, isIMI = false, collection = 'comments' }) {
  const user = useSelector(selectUser);
  const { instance, setCommentsUpdated, reviews } = useOutletContext();
  const location = useLocation();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  // pages provide reviews, other stages will provide an instance.reviews. Just for incase it'll default to an empty array.
  const reviewsToUse = reviews ?? instance?.reviews ?? [];

  useEffect(() => {
    setRightSidebarOpen(location.pathname.includes('/edit'));
  }, [location.pathname]);

  if (!instance) {
    return <BASELoading />;
  }

  // For Page-style sidebar usage (like PageComments)
  if (hasSidebar) {
    const getCurrentReview = () => {
      let currentDate = new Date();

      let currentReview = reviewsToUse.find((review) => {
        let startDate = new Date(review.start);
        let endDate = new Date(review.end);

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        return startDate <= currentDate && endDate >= currentDate;
      });

      // this check will make it so if the review is an internal review and the user is an external reviewer then they can not comment at this moment.
      if (
        currentReview?.internality === true &&
        !authRoles.leadEditor.includes(user.role)
      ) {
        currentReview.commentingBlocked = true;
      }

      return currentReview;
    };

    const currentReview = getCurrentReview();

    const handleCycleName = (cycle) => {
      if (
        cycle.toLowerCase().includes('rev') &&
        currentReview.commentingBlocked
      ) {
        return 'Comments closed, revisions in progress.';
      }

      const name = cycle
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      return name;
    };

    const displayDate = () => {
      return (
        <ListItem className="px-0">
          <ListItemText
            classes={{ root: 'px-8', primary: 'font-medium' }}
            disableTypography
            primary={handleCycleName(currentReview.cycle)}
            secondary={
              <span className="flex flex-col sm:flex-row sm:items-center -ml-2 mt-8 sm:mt-4 space-y-4 sm:space-y-0 sm:space-x-12">
                {currentReview.start && (
                  <span className="flex items-center">
                    <BASESvgIcon size={20}>
                      heroicons-outline:calendar
                    </BASESvgIcon>
                    <Typography
                      component="span"
                      className="mx-6 text-md"
                      color="text.secondary"
                    >
                      {moment
                        .utc(currentReview.start)
                        .local()
                        .format('MMM DD, YYYY') + ' '}
                      -
                      {' ' +
                        moment
                          .utc(currentReview.end)
                          .local()
                          .format('MMM DD, YYYY')}
                    </Typography>
                  </span>
                )}
              </span>
            }
          />
        </ListItem>
      );
    };

    return (
      <div className="relative my-64">
        <div className="flex flex-row h-64 items-center justify-between w-full pl-16">
          <Typography className="text-20 font-bold leading-none">
            Comments
          </Typography>
          <IconButton
            className="m-0"
            sx={{ color: 'black' }}
            component={NavLinkAdapter}
            to={`/pages/${instance.id}`}
            size="large"
          >
            <BASESvgIcon>heroicons-outline:x</BASESvgIcon>
          </IconButton>
        </div>
        <div className="flex flex-row items-center justify-between w-full pl-16">
          {/* <Typography className="text-18 leading-none"> */}
            {!currentReview ? 'No current review.' : displayDate()}
          {/* </Typography> */}
        </div>
        <div className="relative flex flex-col flex-auto px-0">
          <CommentList
            comment_category={category}
            instance={instance}
            simple_list
            setCommentsUpdated={setCommentsUpdated}
          />
        </div>
        <Box
          className="flex justify-between items-center mt-40 p-16 border-t fixed bottom-0 z-9999"
          sx={{
            backgroundColor: 'background.default',
            width: 384
          }}
        >
          <Button
            to={`/lessons/${instance?.section?.lesson?.id}/comments`}
            component={Link}
            className="mb-0"
            variant="outlined"
            startIcon={
              <BASESvgIcon size={20}>
                heroicons-outline:external-link
              </BASESvgIcon>
            }
          >
            All Comments
          </Button>
          <Tooltip
            title={
              !currentReview || currentReview.commentingBlocked
                ? 'Outside Review Period'
                : 'Add Comment'
            }
          >
            <span>
              <Button
                className="ml-8"
                variant="contained"
                color="secondary"
                component={NavLinkAdapter}
                to="new/edit"
                disabled={!currentReview || currentReview.commentingBlocked}
              >
                Add Comment
              </Button>
            </span>
          </Tooltip>
        </Box>
      </div>
    );
  }

  // For full-page layout (like CourseComments, LessonComments, etc.)
  return (
    <Root
      content={
        <Container className="py-32">
          <CommentList
            comment_category={category}
            instance={instance}
            simple_list={false}
            setCommentsUpdated={setCommentsUpdated}
            isIMI={isIMI}
            collection={collection}
          />
        </Container>
      }
      rightSidebarContent={
        <Outlet context={{ instance, reviews: reviewsToUse }} />
      }
      rightSidebarOpen={rightSidebarOpen}
      rightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={400}
      rightSidebarVariant="temporary"
      scroll={isMobile ? 'normal' : 'normal'}
    />
  );
}

export default InstanceComments;
